(function () {
  "use strict";

  var HOST_SELECTOR = "[data-shred-purchase-layout]";

  function normalise(value) {
    return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
  }

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  // Under a translated locale the option names arrive translated, so each lookup
  // has to accept every accepted spelling rather than one English literal.
  var OPTION_ALIASES = {
    supply: ["supply", "suministro"],
    flavor: ["flavor", "flavour", "sabor"]
  };

  function optionIndex(names, wanted) {
    var accepted = (OPTION_ALIASES[normalise(wanted)] || [normalise(wanted)])
      .map(normalise);
    return (names || []).findIndex(function (name) {
      return accepted.indexOf(normalise(name)) !== -1;
    });
  }

  function labelOf(state, key, fallback) {
    var v = state && state.settings ? state.settings[key] : null;
    return v == null || v === "" ? fallback : v;
  }

  function supplyDays(value) {
    var v = String(value || "");
    // English: "30 Day Supply" / "30-day". Spanish: "30 dias de suministro".
    var match = v.match(/\b(\d+)\s*-?\s*(day|d\u00eda|dia)/i);
    // Last resort: the option value always leads with the day count in every
    // locale, so fall back to the first integer rather than returning 0 and
    // rendering "No Supply variants were found."
    if (!match) match = v.match(/\b(\d+)\b/);
    return match ? parseInt(match[1], 10) : 0;
  }

  function expandFlavorMix(value) {
    return String(value || "")
      .split(/\s*\+\s*/)
      .reduce(function (flavors, part) {
        var cleaned = part.trim();
        if (!cleaned) return flavors;
        var match = cleaned.match(/^(\d+)\s*x\s+(.+)$/i);
        var count = match ? Math.max(1, parseInt(match[1], 10) || 1) : 1;
        var flavor = (match ? match[2] : cleaned).trim();
        for (var index = 0; index < count; index++) flavors.push(flavor);
        return flavors;
      }, []);
  }

  function flavorSignature(flavors) {
    return (flavors || []).map(normalise).sort().join("|");
  }

  function money(cents, currency, stripZeroDecimals) {
    var amount = Number(cents || 0) / 100;
    var hasDecimals = Math.round(amount * 100) % 100 !== 0;
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency || "USD",
        currencyDisplay: "narrowSymbol",
        minimumFractionDigits: stripZeroDecimals && !hasDecimals ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (_error) {
      return "$" + amount.toFixed(stripZeroDecimals && !hasDecimals ? 0 : 2);
    }
  }

  function planMatchesDays(plan, days) {
    var name = String((plan && plan.name) || "");
    // The plan name arrives TRANSLATED under a localised storefront. If these
    // patterns miss, plansForGroup() falls back to every plan on the variant and
    // the wrong cadence - and therefore the wrong price - can be selected. Match
    // English and Spanish.
    var patterns = days >= 90
      ? [/\b3\s*(months?|meses|mes)\b/i, /\b12\s*(weeks?|semanas?)\b/i,
         /\b90\s*(days?|d\u00edas?|dias?)\b/i]
      : [/\b1\s*(months?|mes|meses)\b/i, /\b4\s*(weeks?|semanas?)\b/i,
         /\b30\s*(days?|d\u00edas?|dias?)\b/i];
    return patterns.some(function (pattern) {
      return pattern.test(name);
    });
  }

  function plansForGroup(group) {
    var plans = (group && group.selectedVariant && group.selectedVariant.sellingPlans) || [];
    var matching = plans.filter(function (plan) {
      return planMatchesDays(plan, group.days);
    });
    return matching.length ? matching : plans;
  }

  function selectedPlan(group) {
    var plans = plansForGroup(group);
    if (!plans.length) return null;
    return plans.find(function (plan) {
      return String(plan.id) === String(group.selectedPlanId);
    }) || plans[0];
  }

  function cadencePlansForDisplay(group, current) {
    var seen = {};

    // Subscription providers can expose multiple plans with the same customer-facing cadence
    // (for example the 33% control and 40% A/B-test plans). Keep the active
    // plan first and collapse only duplicate labels; checkout still receives
    // the active plan's original ID and price.
    return [current].concat(plansForGroup(group)).filter(function (plan) {
      if (!plan) return false;
      var key = normalise(plan.name) || String(plan.id);
      if (seen[key]) return false;
      seen[key] = true;
      return true;
    });
  }

  function discountPercent(variant, plan) {
    var base = Number((plan && plan.compareAtPrice) || (variant && variant.price) || 0);
    var price = Number((plan && plan.price) || base);
    if (!base || price >= base) return 0;
    return Math.round((1 - price / base) * 100);
  }

  function variantForFlavors(group, flavorIndex, flavors) {
    var wanted = flavorSignature(flavors);
    return group.variants.find(function (variant) {
      return flavorSignature(expandFlavorMix(variant.options[flavorIndex])) === wanted;
    }) || null;
  }

  function prepareGroup(group, flavorIndex, preferredVariant) {
    var available = group.variants.filter(function (variant) {
      return variant.available;
    });
    var candidates = available.length ? available : group.variants;
    group.selectedVariant = preferredVariant && candidates.indexOf(preferredVariant) !== -1
      ? preferredVariant
      : candidates[0] || null;

    var choices = [];
    var seen = {};
    group.variants.forEach(function (variant) {
      expandFlavorMix(variant.options[flavorIndex]).forEach(function (flavor) {
        var key = normalise(flavor);
        if (!key || seen[key]) return;
        seen[key] = true;
        choices.push(flavor);
      });
    });

    var selected = group.selectedVariant
      ? expandFlavorMix(group.selectedVariant.options[flavorIndex])
      : [];
    var slotCount = group.days >= 90 ? 3 : 1;
    var fallback = selected[0] || choices[0] || "";
    while (selected.length < slotCount) selected.push(fallback);

    group.flavorChoices = choices;
    group.selectedFlavors = selected.slice(0, slotCount);
    group.selectedPlanId = null;
  }

  function buildState(host, config) {
    var product = config.product || {};
    var variants = product.variants || [];
    var names = product.optionNames || [];
    var supplyIndex = optionIndex(names, "Supply");
    var flavorIndex = optionIndex(names, "Flavor");

    if (supplyIndex < 0 || flavorIndex < 0) {
      host.innerHTML = '<p class="shred-purchase__error">This layout requires “Supply” and “Flavor” product options.</p>';
      return null;
    }

    var settings = config.settings || {};
    var defaultBuyOne = settings.defaultVariantBuyOne;
    var defaultBuyThree = settings.defaultVariantBuyThree;
    var queryParameters = new URLSearchParams(window.location.search);
    var requestedVariantId = queryParameters.get("variant");
    var requestedSellingPlanId = queryParameters.get("selling_plan");
    var requestedVariant = requestedVariantId && variants.find(function (variant) {
      return String(variant.id) === String(requestedVariantId);
    });
    var groups = {};

    variants.forEach(function (variant) {
      var title = variant.options[supplyIndex];
      var days = supplyDays(title);
      if (!days) return;
      if (!groups[days]) {
        groups[days] = {
          id: "supply-" + days,
          kind: "subscription",
          title: title,
          days: days,
          variants: [],
          selectedVariant: null,
          selectedPlanId: null,
        };
      }
      groups[days].variants.push(variant);
    });

    var options = Object.keys(groups).map(function (key) {
      var group = groups[key];
      var wantedTitle = group.days >= 90 ? defaultBuyThree : defaultBuyOne;
      var requestedGroupVariant = requestedVariant && group.variants.find(function (variant) {
        return String(variant.id) === String(requestedVariant.id);
      });
      var preferredVariant = requestedGroupVariant || group.variants.find(function (variant) {
        return normalise(variant.title) === normalise(wantedTitle);
      }) || null;
      prepareGroup(group, flavorIndex, preferredVariant);
      if (requestedGroupVariant && requestedSellingPlanId && group.selectedVariant === requestedGroupVariant) {
        var requestedPlan = (requestedGroupVariant.sellingPlans || []).find(function (plan) {
          return String(plan.id) === String(requestedSellingPlanId) ||
            (plan.legacyIds || []).some(function (id) { return String(id) === String(requestedSellingPlanId); });
        });
        if (requestedPlan) group.selectedPlanId = String(requestedPlan.id);
      }
      return group;
    }).sort(function (a, b) {
      return b.days - a.days;
    });

    if (!options.length) {
      host.innerHTML = '<p class="shred-purchase__error">No Supply variants were found.</p>';
      return null;
    }

    var selected = requestedVariant && options.find(function (group) {
      return group.selectedVariant && String(group.selectedVariant.id) === String(requestedVariant.id);
    });
    if (!selected) {
      selected = options.find(function (group) {
        return group.days === 90;
      }) || options[0];
    }
    var oneTimeSelected = options.find(function (group) {
      return group.days < 90;
    }) || options[0];

    var state = {
      host: host,
      root: host.closest(".shred-purchase-root"),
      form: host.closest("form"),
      productPrice: (host.closest(".product-details") || host.closest(".shopify-section") || document)
        .querySelector("product-price"),
      installments: (host.closest(".product-details") || document).querySelector(".installments"),
      product: product,
      settings: config.settings || {},
      currency: config.currency || "USD",
      supplyIndex: supplyIndex,
      flavorIndex: flavorIndex,
      options: options,
      selectedId: selected.id,
      selectedByMode: {
        subscription: selected.id,
        onetime: oneTimeSelected.id,
      },
      mode: "subscription",
    };

    host.__kbSub = state;
    return state;
  }

  function selectedGroup(state) {
    return state.options.find(function (group) {
      return group.id === state.selectedId;
    }) || state.options[0];
  }

  function groupEnabled(state, group) {
    if (!group || !group.selectedVariant || !group.selectedVariant.available) return false;
    if (state.mode === "onetime") return group.days < 90;
    return !!selectedPlan(group);
  }

  function ensureValidSelection(state) {
    var current = selectedGroup(state);
    if (groupEnabled(state, current)) {
      state.selectedByMode[state.mode] = current.id;
      return;
    }
    var fallback = state.options.find(function (group) {
      return groupEnabled(state, group);
    });
    if (fallback) {
      state.selectedId = fallback.id;
      state.selectedByMode[state.mode] = fallback.id;
    }
  }

  function flavorImage(state, flavor, fallback) {
    var value = normalise(flavor);
    if (value.indexOf("orange") !== -1) return state.settings.orangeFlavorImage || fallback || state.product.image || "";
    if (value.indexOf("citrus") !== -1 || value.indexOf("apple") !== -1) {
      return state.settings.citrusFlavorImage || fallback || state.product.image || "";
    }
    return fallback || state.product.image || "";
  }

  function offerImageMarkup(state, group) {
    var variant = group.selectedVariant;
    var image = group.days >= 90
      ? (state.settings.ninetyDayOfferImage || (variant && variant.image))
      : (variant && variant.image);
    return '<div class="shred-purchase__offer-media shred-purchase__offer-media--variant">' +
      (image ? '<img src="' + escapeHtml(image) + '" alt="" width="92" height="92">' : "") +
      "</div>";
  }

  function pauseIconMarkup() {
    return '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<g clip-path="url(#shred-pause-icon-clip)"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.3993 6.28087C10.3993 8.79651 8.35995 10.8359 5.84434 10.8359C4.97675 10.836 4.12718 10.5883 3.39556 10.122C2.66395 9.65572 2.08072 8.99016 1.71451 8.20366C1.66884 8.10242 1.59457 8.01675 1.50084 7.95718C1.40711 7.89761 1.298 7.86675 1.18696 7.86839C1.07591 7.86725 0.966956 7.8986 0.873499 7.95859C0.780043 8.01857 0.706162 8.10457 0.660948 8.206C0.624096 8.28526 0.605412 8.37175 0.606259 8.45915C0.607107 8.54656 0.627464 8.63267 0.665846 8.7112C1.12758 9.69458 1.85982 10.5261 2.7769 11.1086C3.69397 11.691 4.75795 12.0002 5.84434 12C9.0028 12 11.5634 9.43945 11.5634 6.28087C11.5634 3.12229 9.0028 0.561791 5.84434 0.561791C5.06141 0.560844 4.28666 0.721106 3.56836 1.0326C2.85006 1.34409 2.20359 1.80014 1.66921 2.37234L1.61413 0.57044C1.61176 0.493199 1.59421 0.417181 1.56246 0.346726C1.53072 0.27627 1.48541 0.212757 1.42912 0.159812C1.37283 0.106868 1.30666 0.0655285 1.2344 0.0381552C1.16213 0.0107819 1.08518 -0.0020896 1.00794 0.000275737C0.930699 0.00264107 0.854681 0.0201969 0.784226 0.0519407C0.71377 0.0836846 0.650257 0.128995 0.597312 0.185285C0.544368 0.241574 0.503028 0.307741 0.475655 0.380007C0.448282 0.452273 0.43541 0.529223 0.437776 0.606463L0.535065 3.78475C0.537602 3.86567 0.556845 3.94519 0.591584 4.01831C0.626323 4.09144 0.675809 4.15659 0.736932 4.20967C0.793662 4.26743 0.861721 4.31282 0.936833 4.34302C1.01195 4.37321 1.09249 4.38755 1.17341 4.38513L4.3517 4.28784C4.42894 4.28547 4.50496 4.26792 4.57541 4.23618C4.64587 4.20443 4.70938 4.15913 4.76233 4.10284C4.81527 4.04655 4.85661 3.98038 4.88399 3.90812C4.91136 3.83585 4.92424 3.7589 4.92187 3.68166C4.91951 3.60442 4.90196 3.5284 4.87021 3.45795C4.83847 3.38749 4.79316 3.32398 4.73687 3.27103C4.68058 3.21809 4.61442 3.17675 4.54215 3.14937C4.46989 3.122 4.39294 3.10912 4.3157 3.11149L2.52069 3.16638C2.94623 2.7111 3.4609 2.34827 4.03269 2.10045C4.60448 1.85263 5.22115 1.72513 5.84434 1.72589C8.35995 1.72589 10.3993 3.76523 10.3993 6.28087Z" fill="#555555"/></g>' +
      '<defs><clipPath id="shred-pause-icon-clip"><rect width="12" height="12" fill="white"/></clipPath></defs></svg>';
  }

  function informationIconMarkup() {
    return '<svg width="14" height="14" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<circle cx="9" cy="9" r="8" stroke="currentColor" stroke-width="1.5"/>' +
      '<path d="M9 8V13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '<circle cx="9" cy="5.25" r="1" fill="currentColor"/></svg>';
  }

  function pauseMarkup(state) {
    if (state.mode !== "subscription" || !state.settings.pauseLabel) return "";
    var information = state.settings.pauseInformation || "";
    var informationId = ((state.root && state.root.id) || "shred-purchase") + "-information";
    return '<div class="shred-purchase__pause-wrap"><p class="shred-purchase__pause">' +
      '<span aria-hidden="true">' + pauseIconMarkup() + '</span><span>' + escapeHtml(state.settings.pauseLabel) + "</span>" +
      (information
        ? '<button type="button" class="shred-purchase__info-toggle" data-shred-info-toggle aria-label="Subscription information" aria-controls="' +
          escapeHtml(informationId) + '" aria-expanded="false">' + informationIconMarkup() + "</button>"
        : "") + "</p>" +
      (information
        ? '<div class="shred-purchase__info-panel rte" id="' + escapeHtml(informationId) +
          '" data-shred-info-panel role="dialog" aria-label="Subscription information" hidden>' + information + "</div>"
        : "") + "</div>";
  }

  function cadenceArrowMarkup() {
    return '<svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M1 1.5L6 6.5L11 1.5" stroke="#222222" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }

  function iconMarkup(state, name) {
    var template = state.root && state.root.querySelector('[data-shred-trust-icon="' + name + '"]');
    return template ? template.innerHTML : "";
  }

  function trustItems(state, group, discount) {
    var s = state.settings;
    if (state.mode === "onetime") return [];

    if (group.days >= 90) {
      return [
        ["check", s.tagOutOfStockProtection],
        ["discount", discount + "% off"],
        ["shipping", s.tagFreeShipping],
        ["meal", s.tagFreeMealPlan],
        ["training", s.tagFreeTrainingPlan],
        ["shaker", s.tagFreeShaker],
        ["results", s.tagBestResults],
        ["never", s.tagNeverRunOut],
        ["guarantee", s.tagResultsGuarantee],
      ];
    }

    return [
      ["check", s.tagOutOfStockProtection],
      ["discount", discount + "% off"],
      ["results", s.tagBestResults],
      ["never", s.tagNeverRunOut],
    ];
  }

  function trustMarkup(state, group, discount) {
    var items = trustItems(state, group, discount).filter(function (item) {
      return item[1];
    });
    if (!items.length) return "";
    return '<ul class="shred-purchase__trust-tags" aria-label="Purchase benefits">' +
      items.map(function (item) {
        return '<li><span class="shred-purchase__trust-icon" aria-hidden="true">' +
          iconMarkup(state, item[0]) + '</span><span>' + escapeHtml(item[1]) + "</span></li>";
      }).join("") +
      "</ul>";
  }

  function flavorSelectorsMarkup(state, group) {
    var label = group.days >= 90
      ? state.settings.flavorThreeLabel
      : state.settings.flavorOneLabel;
    return '<div class="shred-purchase__flavors"><p class="shred-purchase__flavor-heading">' +
      escapeHtml(label) + '</p><div class="shred-purchase__flavor-list">' +
      group.selectedFlavors.map(function (selected, slotIndex) {
        var image = flavorImage(state, selected, group.selectedVariant && group.selectedVariant.image);
        var flavorLabel = "Flavor " + (slotIndex + 1);
        return '<div class="shred-purchase__flavor-row">' +
          (image ? '<img src="' + escapeHtml(image) + '" alt="" width="54" height="54">' : "") +
          '<div class="shred-purchase__flavor-dropdown shred-purchase__dropdown">' +
          '<button type="button" class="shred-purchase__flavor-trigger" data-shred-dropdown-toggle aria-haspopup="listbox" aria-expanded="false" aria-label="' +
          escapeHtml(flavorLabel + ", " + selected) + '"><span>' + escapeHtml(selected) + '</span>' + cadenceArrowMarkup() + '</button>' +
          '<div class="shred-purchase__flavor-menu shred-purchase__dropdown-menu" data-shred-dropdown-menu role="listbox" aria-label="' +
          escapeHtml(flavorLabel) + '" hidden>' +
          group.flavorChoices.map(function (choice) {
            var isSelected = normalise(choice) === normalise(selected);
            return '<button type="button" role="option" class="shred-purchase__flavor-option shred-purchase__dropdown-option' +
              (isSelected ? " is-selected" : "") + '" data-shred-dropdown-option data-shred-flavor-option data-option-id="' +
              escapeHtml(group.id) + '" data-slot-index="' + slotIndex + '" data-flavor-value="' + escapeHtml(choice) +
              '" aria-selected="' + (isSelected ? "true" : "false") + '">' + escapeHtml(choice) + "</button>";
          }).join("") +
          "</div></div></div>";
      }).join("") +
      "</div></div>";
  }

  function cardMarkup(state, group) {
    var selected = group.id === state.selectedId;
    var enabled = groupEnabled(state, group);
    var variant = group.selectedVariant;
    var plan = state.mode === "subscription" ? selectedPlan(group) : null;
    var price = plan ? Number(plan.price) : Number((variant && variant.price) || 0);
    var comparePrice = plan
      ? Number(plan.compareAtPrice || (variant && variant.price) || price)
      : price;
    var discount = plan ? discountPercent(variant, plan) : 0;
    var title = group.days >= 90
      ? (state.mode === "subscription" ? state.settings.buyThreeAutoshipLabel : state.settings.buyThreeOnetimeLabel)
      : state.settings.buyOneLabel;
    var dayPrice = Math.round(price / Math.max(1, group.days));
    var showBestValue = state.mode === "subscription" && group.days >= 90;
    var showStandardPrice = state.mode === "onetime" && group.days < 90 && state.settings.standardPriceLabel;

    return '<div class="shred-purchase__card' +
      (selected ? " is-selected" : "") +
      (!enabled ? " is-disabled" : "") +
      '" data-shred-offer="' + escapeHtml(group.id) + '" role="radio" aria-checked="' +
      (selected ? "true" : "false") + '" aria-disabled="' + (!enabled ? "true" : "false") +
      '" tabindex="' + (enabled ? "0" : "-1") + '">' +
      (showBestValue && state.settings.bestValueLabel
        ? '<span class="shred-purchase__best-value">' + escapeHtml(state.settings.bestValueLabel) + "</span>"
        : "") +
      '<div class="shred-purchase__summary">' +
      offerImageMarkup(state, group) +
      '<div class="shred-purchase__summary-copy"><div class="shred-purchase__title-row"><strong class="shred-purchase__offer-title">' +
      escapeHtml(title) + "</strong>" +
      (discount ? '<span class="shred-purchase__save">' + escapeHtml(labelOf(state, "saveLabel", "SAVE")) + " " + discount + "%</span>" : "") +
      (showStandardPrice ? '<span class="shred-purchase__standard-price">' + escapeHtml(state.settings.standardPriceLabel) + "</span>" : "") +
      '</div><p class="shred-purchase__supply-row" data-shred-supply-details><strong>' + escapeHtml(group.days + " " + labelOf(state, "supplyUnitLabel", "day supply")) +
      '</strong><span class="shred-purchase__split" aria-hidden="true">/</span><strong>' + escapeHtml(money(dayPrice, state.currency, true)) +
      " " + escapeHtml(labelOf(state, "perDayLabel", "per day")) + '</strong></p></div><div class="shred-purchase__prices"><strong>' +
      escapeHtml(money(price, state.currency, true)) + "</strong>" +
      (discount && comparePrice > price ? "<s>" + escapeHtml(money(comparePrice, state.currency, true)) + "</s>" : "") +
      "</div></div>" +
      (selected && enabled
        ? '<div class="shred-purchase__expanded">' + flavorSelectorsMarkup(state, group) + trustMarkup(state, group, discount) + "</div>"
        : "") +
      "</div>";
  }

  function cadenceMarkup(state) {
    if (state.mode !== "subscription") return "";
    var group = selectedGroup(state);
    var plans = plansForGroup(group);
    if (!plans.length) return "";
    var current = selectedPlan(group);
    var displayPlans = cadencePlansForDisplay(group, current);
    return '<div class="shred-purchase__cadence"><span>' +
      escapeHtml(state.settings.deliveredEveryLabel) + '</span><div class="shred-purchase__cadence-dropdown shred-purchase__dropdown">' +
      '<button type="button" class="shred-purchase__cadence-trigger" data-shred-dropdown-toggle data-shred-cadence-toggle aria-haspopup="listbox" aria-expanded="false">' +
      '<span>' + escapeHtml(current.name) + '</span>' + cadenceArrowMarkup() + '</button>' +
      '<div class="shred-purchase__cadence-menu shred-purchase__dropdown-menu" data-shred-dropdown-menu role="listbox" hidden>' +
      displayPlans.map(function (plan) {
        var selected = String(plan.id) === String(current.id);
        return '<button type="button" role="option" data-shred-dropdown-option data-shred-cadence-option data-plan-id="' + escapeHtml(plan.id) +
          '" aria-selected="' + (selected ? "true" : "false") + '" class="shred-purchase__cadence-option shred-purchase__dropdown-option' +
          (selected ? " is-selected" : "") + '">' + escapeHtml(plan.name) + "</button>";
      }).join("") +
      "</div></div></div>";
  }

  function render(state, announce) {
    ensureValidSelection(state);
    var group = selectedGroup(state);
    var autoshipGroup = state.options.find(function (option) {
      return option.id === state.selectedByMode.subscription;
    }) || group;
    var autoshipVariant = autoshipGroup && autoshipGroup.selectedVariant;
    var autoshipPlan = selectedPlan(autoshipGroup);
    var discount = autoshipPlan ? discountPercent(autoshipVariant, autoshipPlan) : 0;

    state.host.innerHTML = '<div class="shred-purchase">' +
      '<div class="shred-purchase__heading"><span></span><strong>' + escapeHtml(state.settings.heading) + "</strong><span></span></div>" +
      '<div class="shred-purchase__tabs" role="tablist" aria-label="Purchase type">' +
      '<button type="button" role="tab" data-shred-mode="subscription" aria-selected="' +
      (state.mode === "subscription" ? "true" : "false") + '" class="' +
      (state.mode === "subscription" ? "is-active" : "") + '"><span>' + escapeHtml(state.settings.autoshipLabel) + "</span>" +
      (discount ? "<em>" + escapeHtml(labelOf(state, "saveLabel", "SAVE")) + " " + discount + "%</em>" : "") + "</button>" +
      '<button type="button" role="tab" data-shred-mode="onetime" aria-selected="' +
      (state.mode === "onetime" ? "true" : "false") + '" class="' +
      (state.mode === "onetime" ? "is-active" : "") + '"><span>' + escapeHtml(state.settings.oneTimeLabel) + "</span></button></div>" +
      pauseMarkup(state) +
      '<div class="shred-purchase__cards" role="radiogroup" aria-label="Supply">' +
      state.options.filter(function (option) {
        return state.mode !== "onetime" || option.days < 90;
      }).map(function (option) { return cardMarkup(state, option); }).join("") +
      "</div>" + cadenceMarkup(state) + "</div>";

    syncForm(state);
    syncProductPrice(state);
    syncInstallments(state);
    if (announce) announceChange(state);
  }

  function ensureInput(form, name, marker) {
    var input = marker ? form.querySelector('input[' + marker + ']') : form.querySelector('input[name="' + name + '"]');
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      if (marker) input.setAttribute(marker, "");
      form.appendChild(input);
    }
    return input;
  }

  function syncForm(state) {
    var form = state.form;
    var group = selectedGroup(state);
    var variant = group && group.selectedVariant;
    if (!form || !variant) return;

    var variantInput = form.querySelector('input[name="id"]') || ensureInput(form, "id");
    variantInput.value = String(variant.id);
    variantInput.disabled = false;

    var quantityInput = ensureInput(form, "quantity", "data-shred-quantity");
    form.querySelectorAll('[name="quantity"]').forEach(function (input) {
      if (input !== quantityInput) input.disabled = true;
    });
    quantityInput.value = "1";
    quantityInput.disabled = false;

    var sellingPlanInput = ensureInput(form, "selling_plan", "data-shred-selling-plan");
    form.querySelectorAll('[name="selling_plan"]').forEach(function (input) {
      if (input !== sellingPlanInput) input.disabled = true;
    });
    var plan = state.mode === "subscription" ? selectedPlan(group) : null;
    sellingPlanInput.value = plan ? String(plan.id) : "";
    sellingPlanInput.disabled = !plan;

    var addToCart = form.querySelector("add-to-cart-component");
    if (addToCart && variant.image) addToCart.dataset.productVariantMedia = variant.image;
  }

  function syncProductPrice(state) {
    var productPrice = state.productPrice;
    var group = selectedGroup(state);
    var variant = group && group.selectedVariant;
    if (!productPrice || !variant) return;

    var plan = state.mode === "subscription" ? selectedPlan(group) : null;
    var price = plan ? Number(plan.price) : Number(variant.price || 0);
    var priceElement = productPrice.querySelector('[ref="priceContainer"] .price');
    if (!priceElement) return;
    priceElement.textContent = money(price, state.currency, true);

    productPrice.dataset.variantId = String(variant.id);
    productPrice.classList.add("price-ready");
  }

  function syncInstallments(state) {
    var installments = state.installments;
    var group = selectedGroup(state);
    var variant = group && group.selectedVariant;
    if (!installments || !group || !variant) return;
    var plan = state.mode === "subscription" ? selectedPlan(group) : null;
    var price = plan ? Number(plan.price) : Number(variant.price || 0);
    var dayPrice = Math.round(price / Math.max(1, group.days));
    installments.textContent = group.days + " " + labelOf(state, "supplyUnitLabel", "day supply") +
      " / " + money(dayPrice, state.currency, true) + " " + labelOf(state, "perDayLabel", "per day");
    installments.style.display = "";
  }

  function announceChange(state) {
    var group = selectedGroup(state);
    var variant = group && group.selectedVariant;
    if (!group || !variant) return;
    state.host.dispatchEvent(new CustomEvent("kb-sub:option-change", {
      bubbles: true,
      detail: {
        id: group.id,
        kind: state.mode,
        days: group.days,
        title: group.title,
        variantId: variant.id,
        variantTitle: variant.options[state.flavorIndex],
        mediaId: variant.mediaId,
        image: variant.image,
      },
    }));
  }

  function selectFlavor(state, option) {
    var optionGroupId = option.getAttribute("data-option-id");
    var group = state.options.find(function (groupOption) {
      return groupOption.id === optionGroupId;
    });
    if (!group) return;
    var slotIndex = parseInt(option.getAttribute("data-slot-index"), 10) || 0;
    var nextFlavors = group.selectedFlavors.slice();
    nextFlavors[slotIndex] = option.getAttribute("data-flavor-value");
    var variant = variantForFlavors(group, state.flavorIndex, nextFlavors);
    if (!variant || !variant.available) {
      render(state, false);
      return;
    }
    group.selectedFlavors = nextFlavors;
    group.selectedVariant = variant;
    group.selectedPlanId = null;
    render(state, true);
  }

  function closeDropdowns(state, exceptToggle) {
    state.host.querySelectorAll("[data-shred-dropdown-toggle]").forEach(function (toggle) {
      if (toggle === exceptToggle) return;
      toggle.setAttribute("aria-expanded", "false");
      var dropdown = toggle.closest(".shred-purchase__dropdown");
      var menu = dropdown && dropdown.querySelector("[data-shred-dropdown-menu]");
      if (menu) menu.hidden = true;
    });
  }

  function closeInformation(state, returnFocus) {
    var toggle = state.host.querySelector("[data-shred-info-toggle]");
    var panel = state.host.querySelector("[data-shred-info-panel]");
    if (!toggle || !panel || panel.hidden) return;
    toggle.setAttribute("aria-expanded", "false");
    panel.hidden = true;
    if (returnFocus) toggle.focus();
  }

  function bind(state) {
    if (state.form) {
      // Restore the selected variant/plan immediately before any cart handler
      // serializes the form. App widgets can mount after our initial render.
      state.form.addEventListener("submit", function () { syncForm(state); }, true);
      state.form.addEventListener("formdata", function (event) {
        var group = selectedGroup(state);
        var plan = state.mode === "subscription" ? selectedPlan(group) : null;
        if (plan) event.formData.set("selling_plan", String(plan.id));
        else event.formData.delete("selling_plan");
      });
    }

    state.host.addEventListener("click", function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      var informationToggle = target.closest("[data-shred-info-toggle]");
      if (informationToggle) {
        event.stopPropagation();
        var informationPanel = state.host.querySelector("[data-shred-info-panel]");
        if (!informationPanel) return;
        var willOpenInformation = informationPanel.hidden;
        closeDropdowns(state);
        informationToggle.setAttribute("aria-expanded", willOpenInformation ? "true" : "false");
        informationPanel.hidden = !willOpenInformation;
        return;
      }
      var mode = target.closest("[data-shred-mode]");
      if (mode) {
        state.selectedByMode[state.mode] = state.selectedId;
        state.mode = mode.getAttribute("data-shred-mode") === "onetime" ? "onetime" : "subscription";
        state.selectedId = state.selectedByMode[state.mode] || state.selectedId;
        render(state, true);
        return;
      }
      var flavorOption = target.closest("[data-shred-flavor-option]");
      if (flavorOption) {
        selectFlavor(state, flavorOption);
        return;
      }
      var cadenceOption = target.closest("[data-shred-cadence-option]");
      if (cadenceOption) {
        selectedGroup(state).selectedPlanId = cadenceOption.getAttribute("data-plan-id");
        render(state, true);
        return;
      }
      var dropdownToggle = target.closest("[data-shred-dropdown-toggle]");
      if (dropdownToggle) {
        event.stopPropagation();
        closeInformation(state, false);
        var dropdown = dropdownToggle.closest(".shred-purchase__dropdown");
        var dropdownMenu = dropdown.querySelector("[data-shred-dropdown-menu]");
        var willOpen = dropdownToggle.getAttribute("aria-expanded") !== "true";
        closeDropdowns(state, dropdownToggle);
        dropdownToggle.setAttribute("aria-expanded", willOpen ? "true" : "false");
        dropdownMenu.hidden = !willOpen;
        if (willOpen) {
          var selectedDropdownOption = dropdownMenu.querySelector('[aria-selected="true"]') || dropdownMenu.querySelector("button");
          if (selectedDropdownOption) selectedDropdownOption.focus();
        }
        return;
      }
      if (target.closest("select")) return;
      var card = target.closest("[data-shred-offer]");
      if (!card || card.getAttribute("aria-disabled") === "true") return;
      state.selectedId = card.getAttribute("data-shred-offer");
      state.selectedByMode[state.mode] = state.selectedId;
      render(state, true);
    });

    document.addEventListener("click", function (event) {
      var target = event.target instanceof Element ? event.target : event.target && event.target.parentElement;
      if (target && state.host.contains(target) && target.closest(".shred-purchase__dropdown, [data-shred-info-panel]")) return;
      closeDropdowns(state);
      closeInformation(state, false);
    });

    state.host.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && state.host.querySelector("[data-shred-info-panel]:not([hidden])")) {
        closeInformation(state, true);
        return;
      }
      var dropdown = event.target.closest(".shred-purchase__dropdown");
      if (dropdown && event.key === "Escape") {
        var dropdownTrigger = dropdown.querySelector("[data-shred-dropdown-toggle]");
        var dropdownMenu = dropdown.querySelector("[data-shred-dropdown-menu]");
        dropdownTrigger.setAttribute("aria-expanded", "false");
        dropdownMenu.hidden = true;
        dropdownTrigger.focus();
        return;
      }
      if (event.target.matches("[data-shred-dropdown-option]") && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
        event.preventDefault();
        var dropdownOptions = Array.from(dropdown.querySelectorAll("[data-shred-dropdown-option]"));
        var dropdownIndex = dropdownOptions.indexOf(event.target);
        var direction = event.key === "ArrowDown" ? 1 : -1;
        dropdownOptions[(dropdownIndex + direction + dropdownOptions.length) % dropdownOptions.length].focus();
        return;
      }
      if (event.key !== "Enter" && event.key !== " ") return;
      if (event.target.closest("button, select")) return;
      var card = event.target.closest("[data-shred-offer]");
      if (!card || card.getAttribute("aria-disabled") === "true") return;
      event.preventDefault();
      state.selectedId = card.getAttribute("data-shred-offer");
      state.selectedByMode[state.mode] = state.selectedId;
      render(state, true);
    });
  }

  function initHost(host) {
    if (!host || host.__shredPurchaseInitialised) return;
    var root = host.closest(".shred-purchase-root");
    var script = root && root.querySelector("[data-shred-purchase-config]");
    if (!script) return;
    var config;
    try {
      config = JSON.parse(script.textContent);
    } catch (_error) {
      host.innerHTML = '<p class="shred-purchase__error">Purchase options could not be loaded.</p>';
      return;
    }
    var state = buildState(host, config);
    if (!state) return;
    host.__shredPurchaseInitialised = true;
    bind(state);
    render(state, true);
  }

  function init(scope) {
    var root = scope || document;
    if (root.matches && root.matches(HOST_SELECTOR)) initHost(root);
    root.querySelectorAll(HOST_SELECTOR).forEach(initHost);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { init(document); }, { once: true });
  } else {
    init(document);
  }

  document.addEventListener("shopify:section:load", function (event) {
    init(event.target);
  });
})();
